import { isInIframe } from '@shared/utils/isInIframe';
import { useEventUserRole } from '@shared/components/AuthProvider';
import type { PageKind } from '@graphql/generated-base';
import { findCustomPageBySlug, selectRoutedCustomPage, toPageContainerSlug } from '@shared/utils/migratedCustomPages';
import { useCurrentPageSlug } from '@shared/utils/hooks/useCurrentPageSlug';
import { useGuestPageContainerBySlugQuery } from '../Custom.graphql.generated';
import { useIsStaysRedesignActive } from '../components/Honeymoon/useIsStaysRedesignActive';

type CustomDataPage = Readonly<{ pageSlug: string; pageKind?: PageKind | null }>;

// toPageContainerSlug aligns this hook's cache key with useFetchCustomPageData
// (registrylinks → registry; no-op elsewhere). `pages` carries the page kinds so a userCustom
// squatter (CORE-6456) is fetched by its literal slug and matches useFetchCustomPageData's cache key.
export const useCustomData = (_eventId: string, eventHandle: string, pageSlug: string, pages?: ReadonlyArray<CustomDataPage>) => {
  const isPreviewing = isInIframe();
  const { isAdmin } = useEventUserRole();
  const { currentPageSlug, isUrlBasedCustomPageSlug } = useCurrentPageSlug(eventHandle);

  // PRNT-4817: `/{eventHandle}/page/{slug}` — the URL the admin preview iframe is pointed at for a
  // contested slug — names one specific page, so it is the one call site allowed to tell the server
  // which of two same-slug containers it wants. The canonical `/{eventHandle}/{slug}` URL must send
  // no `pageKind`: naming one both overrides route ownership and skips the server's canonical-route
  // withholding rule (PRNT-4815), which is what keeps a migrated archive off the couple's URL.
  const isRoutedAtThisPage = isUrlBasedCustomPageSlug && !!currentPageSlug && currentPageSlug.toLowerCase() === pageSlug.toLowerCase();
  const routedPageKind = (isRoutedAtThisPage ? selectRoutedCustomPage(pages, pageSlug)?.pageKind : undefined) ?? undefined;
  const containerSlug = toPageContainerSlug(pageSlug, routedPageKind ?? findCustomPageBySlug(pages, pageSlug)?.pageKind);
  const isStaysRedesignActive = useIsStaysRedesignActive();

  const {
    data: pageContainerData,
    loading,
    error,
  } = useGuestPageContainerBySlugQuery({
    variables: {
      payload: {
        eventName: eventHandle,
        pageSlug: containerSlug,
      },
      isAdminDashboard: isPreviewing && isAdmin,
      // Undefined is dropped from the request, so the canonical route's cache key is unchanged.
      pageKind: routedPageKind,
    },
    batchMode: 'fast',
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: false,
    // SSR participation scoped to redesign — non-redesign keeps ssr=false (matches develop).
    ssr: isStaysRedesignActive,
    skip: !containerSlug,
  });

  return {
    loading,
    pageContainerBySlug: pageContainerData?.pageContainerBySlug,
    error,
    isAdmin,
  };
};
